<template>
  <div class="flex flex-col">
    <div class="label">{{ t('search.selectFiltersComponent.price') }}</div>
    <div class="w-full mt-3 mx-0 price-slider">
      <Slider
        ref="sliderRange"
        v-model="price"
        :max="PRICE_MAX"
        :lazy="false"
        :format="format"
        :tooltips="false"
        :classes="sliderClasses"
        :options="sliderOptions"
        @change="
          (e: [number, number]) =>
            emit('updated', { priceMin: e[0], priceMax: e[1] })
        "
      />
      <div class="grid grid-cols-2 gap-8">
        <div class="w-3/4">
          <input
            v-model="priceMinDisplay"
            type="text"
            class="border rounded-md py-0 px-4 text-center mx-0 mt-4 w-full h-8"
            @focus="handlePriceMinFocus"
            @blur="handlePriceMinBlur"
          />
        </div>
        <div class="w-3/4 ml-auto">
          <input
            v-model="priceMaxDisplay"
            type="text"
            class="border rounded-md py-0 px-4 text-center mx-0 mt-4 w-full h-8"
            @focus="handlePriceMaxFocus"
            @blur="handlePriceMaxBlur"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Slider from '@vueform/slider'
import { formatMoney } from '~/utils/currencyHelpers'
import {
  parsePriceInput,
  parsePriceToNumber,
  processPriceMinInput,
  processPriceMaxInput,
} from '~/utils/priceInputHelpers'

const PRICE_MIN = 0
const PRICE_MAX = 1200

const { t } = useI18n()

const {
  priceMin: priceMinProp = PRICE_MIN,
  priceMax: priceMaxProp = PRICE_MAX,
} = defineProps<{
  priceMin?: number
  priceMax?: number
}>()

const emit = defineEmits<{
  updated: [payload: { priceMin: number; priceMax: number }]
}>()

const sliderRange = ref<InstanceType<typeof Slider> | null>(null)
const isPriceMinFocused = ref(false)
const isPriceMaxFocused = ref(false)
const priceMinAtFocus = ref<number | null>(null)
const priceMaxAtFocus = ref<number | null>(null)

onMounted(() => {
  if (sliderRange.value?.$el) {
    sliderRange.value.$el
      .querySelectorAll('.tooltip-text')
      .forEach((item: Element, index: number) => {
        item.classList.add('tooltip-text--' + (index + 1))
      })
  }
})

const format = (value: number) => formatMoney(value)

const sliderOptions = {
  range: {
    min: [PRICE_MIN],
    '10%': [20, 1],
    '60%': [150, 1],
    '80%': [900, 1],
    max: [PRICE_MAX],
  },
}

const sliderClasses = {
  target:
    'relative box-border select-none touch-none tap-highlight-transparent touch-callout-none disabled:cursor-not-allowed',
  focused: 'slider-focused',
  tooltipFocus: 'slider-tooltip-focus',
  tooltipDrag: 'slider-tooltip-drag',
  horizontal: 'slider-horizontal h-1.5',
  base: 'w-full h-full relative z-1 bg-gray-300 rounded',
  connects: 'w-full h-full relative overflow-hidden z-0 rounded',
  connect:
    'absolute z-1 top-0 right-0 transform-origin-0 transform-style-flat h-full w-full bg-teal-900 cursor-pointer tap:duration-300 tap:transition-transform disabled:bg-gray-400 disabled:cursor-not-allowed',
  origin:
    'slider-origin absolute z-1 top-0 right-0 transform-origin-0 transform-style-flat h-full w-full h:h-0 v:-top-full txt-rtl-h:left-0 txt-rtl-h:right-auto v:w-0 tap:duration-300 tap:transition-transform',
  handle:
    'bg-teal-50 border border-teal-900 absolute rounded-full cursor-grab focus:outline-none h:w-[13px] h:h-[13px] h:-top-[4px] h:-right-[6px] txt-rtl-h:-left-[4px] txt-rtl-h:right-auto v:w-[13px] v:h-[13px] v:-top-[4px] v:-right-[4px] disabled:cursor-not-allowed focus:ring focus:ring-primary-active focus:ring-opacity-30',
  touchArea: 'h-full w-full',
  tooltip:
    'tooltip-text absolute block text-sm text-center font-semibold whitespace-nowrap text-gray-600',
  tooltipTop: 'bottom-4 merge-h:bottom-3',
  tooltipHidden: 'slider-tooltip-hidden',
  active: 'slider-active shadow-slider-active cursor-grabbing',
  draggable: 'cursor-ew-resize v:cursor-ns-resize',
  tap: 'slider-state-tap',
  drag: 'slider-state-drag',
}

const price = ref([priceMinProp ?? PRICE_MIN, priceMaxProp ?? PRICE_MAX])
const priceMinInput = ref(String(priceMinProp ?? PRICE_MIN))
const priceMaxInput = ref(String(priceMaxProp ?? PRICE_MAX))

watch(
  () => priceMinProp,
  (newVal) => {
    if (newVal !== undefined) {
      price.value[0] = newVal
      if (!isPriceMinFocused.value) {
        priceMinInput.value = String(newVal)
      }
    }
  }
)

watch(
  () => priceMaxProp,
  (newVal) => {
    if (newVal !== undefined) {
      price.value[1] = newVal
      if (!isPriceMaxFocused.value) {
        priceMaxInput.value = String(newVal)
      }
    }
  }
)

const handlePriceMinFocus = (e: FocusEvent) => {
  isPriceMinFocused.value = true
  priceMinAtFocus.value = price.value[0]
  priceMinInput.value = String(price.value[0])
  const target = e.target as HTMLInputElement
  setTimeout(() => {
    const numericValue = priceMinInput.value
    if (target && target.setSelectionRange) {
      target.setSelectionRange(numericValue.length, numericValue.length)
    }
  }, 0)
}

const handlePriceMaxFocus = (e: FocusEvent) => {
  isPriceMaxFocused.value = true
  priceMaxAtFocus.value = price.value[1]
  priceMaxInput.value = String(price.value[1])
  const target = e.target as HTMLInputElement
  setTimeout(() => {
    const numericValue = priceMaxInput.value
    if (target && target.setSelectionRange) {
      target.setSelectionRange(numericValue.length, numericValue.length)
    }
  }, 0)
}

const handlePriceMinBlur = () => {
  isPriceMinFocused.value = false

  const inputNumericValue = parsePriceToNumber(
    parsePriceInput(priceMinInput.value)
  )
  const userTyped =
    priceMinAtFocus.value !== null &&
    inputNumericValue !== priceMinAtFocus.value

  if (userTyped) {
    const validatedValue = processPriceMinInput(
      priceMinInput.value,
      price.value[1]
    )
    price.value[0] = validatedValue
    priceMinInput.value = String(validatedValue)
    emit('updated', { priceMin: validatedValue, priceMax: price.value[1] })
  } else {
    priceMinInput.value = String(price.value[0])
  }

  priceMinAtFocus.value = null
}

const handlePriceMaxBlur = () => {
  isPriceMaxFocused.value = false

  const inputNumericValue = parsePriceToNumber(
    parsePriceInput(priceMaxInput.value)
  )
  const userTyped =
    priceMaxAtFocus.value !== null &&
    inputNumericValue !== priceMaxAtFocus.value

  if (userTyped) {
    const validatedValue = processPriceMaxInput(
      priceMaxInput.value,
      price.value[0]
    )
    price.value[1] = validatedValue
    priceMaxInput.value = String(validatedValue)
    emit('updated', { priceMin: price.value[0], priceMax: validatedValue })
  } else {
    priceMaxInput.value = String(price.value[1])
  }

  priceMaxAtFocus.value = null
}

const priceMinDisplay = computed({
  get: () => {
    if (isPriceMinFocused.value) {
      return priceMinInput.value
    }
    return `${price.value[0]} €`
  },
  set: (value: string) => {
    priceMinInput.value = parsePriceInput(value)
  },
})

const priceMaxDisplay = computed({
  get: () => {
    if (isPriceMaxFocused.value) {
      return priceMaxInput.value
    }
    return `${price.value[1]} €`
  },
  set: (value: string) => {
    priceMaxInput.value = parsePriceInput(value)
  },
})
</script>

<style lang="postcss" scoped>
.label {
  @apply h-9 font-medium text-black flex items-center;
}

.price-slider {
  :deep(.tooltip-text--1) {
    left: 0;
  }

  :deep(.tooltip-text--2) {
    right: 0;
  }
}
</style>
